import {
  Text,
  Flex,
  Button,
  PasswordInput,
  PasswordStrength,
  PasswordRequirements,
  Loader,
} from '@components';
import { useResetPasswordMutation } from '@features/api/userSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, useTheme } from '@mui/material';
import { ResetPaswordFormData } from '@pages/ResetPassword/ResetPassword.types';
import { calculatePasswordStrength } from '@pages/SignUp/SignUp.utils';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { resetPasswordFormSchema } from '@validationSchemas/resetPassword';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { StyledButton } from './PasswordResetRequest.styles';
import { Subtitle } from '@components/InfoCard/InfoCard.styles';

const PasswordResetRequest = () => {
  const intl = useIntl();
  const theme = useTheme();

  const [resetPasswordMutation, { error, isSuccess, isLoading, isError }] =
    useResetPasswordMutation();

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ResetPaswordFormData>({
    resolver: yupResolver(resetPasswordFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
  });
  const password = watch('password');
  const passwordsDifferent =
    errors.confirmPassword?.type === 'oneOf' && password;

  const onSubmit = async (data: ResetPaswordFormData) => {
    if (!token || !data.password) {
      toast(intl.formatMessage({ id: 'misc.somethingWentWrong' }), {
        type: 'success',
      });

      return;
    }

    try {
      await resetPasswordMutation({
        token,
        newPassword: data.password,
      }).unwrap();

      toast(intl.formatMessage({ id: 'profile.passwordChangedMessage' }), {
        type: 'success',
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (isError || !token) {
    if (
      (error as any)?.data?.detail === 'PASSWORD RESET REQUEST EXPIRED' ||
      (error as any)?.data?.detail ===
        'PASSWORD RESET REQUEST DOES NOT EXIST' ||
      (error as any)?.data?.detail === 'PASSWORD RESET REQUEST ALREADY USED'
    ) {
      return (
        <Flex
          height='100%'
          flexDirection='column'
          gap={12}
          alignItems='center'
          justifyContent='center'
        >
          <Text variant='h4' fontWeight='fontWeightMedium'>
            <FormattedMessage id='passwordResetRequest.errorRequestExpired' />
          </Text>
          <Button
            variant='primary'
            onClick={() => (window.location.href = '/login')}
            style={{ marginLeft: 8 }}
          >
            <FormattedMessage id='passwordResetRequest.goToLogin' />
          </Button>
        </Flex>
      );
    }

    return (
      <Flex
        height='100%'
        flexDirection='column'
        gap={12}
        alignItems='center'
        justifyContent='center'
      >
        <Text variant='h4' fontWeight='fontWeightMedium'>
          <FormattedMessage id='passwordResetRequest.somethingWentWrong' />
        </Text>
        <Button
          variant='primary'
          onClick={() => (window.location.href = '/login')}
          style={{ marginLeft: 8 }}
        >
          <FormattedMessage id='passwordResetRequest.goToLogin' />
        </Button>
      </Flex>
    );
  }

  if (isSuccess) {
    return (
      <Flex
        height='100%'
        flexDirection='column'
        gap={12}
        alignItems='center'
        justifyContent='center'
      >
        <Text variant='h4' fontWeight='fontWeightMedium'>
          <FormattedMessage id='passwordResetRequest.passwordReset' />
        </Text>
        <Button
          variant='primary'
          onClick={() => (window.location.href = '/login')}
          style={{ marginLeft: 8 }}
        >
          <FormattedMessage id='passwordResetRequest.goToLogin' />
        </Button>
      </Flex>
    );
  }

  if (isLoading) {
    return <Loader isLoading />;
  }

  return (
    <>
      <Box mt={8} mb={12}>
        <Text variant='h4' fontWeight='fontWeightBold'>
          <FormattedMessage id='passwordResetRequest.title' />
        </Text>
        <Subtitle variant='h5' fontWeight='fontWeightRegular'>
          <FormattedMessage id='passwordResetRequest.subtitle' />
        </Subtitle>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex flexDirection='column' gap={12} mt={10}>
          <PasswordInput
            label={intl.formatMessage({ id: 'misc.password' })}
            autoComplete='new-password'
            {...registerInput(register, 'password')}
            inputProps={{
              'data-testid': 'new-password',
            }}
            error={
              errors.password?.type === 'required'
                ? getErrorProps(intl, errors.password).error
                : undefined
            }
            helperText={
              errors.password?.type === 'required'
                ? getErrorProps(intl, errors.password).helperText
                : undefined
            }
          />
          <PasswordInput
            label={intl.formatMessage({ id: 'misc.confirmNewPassword' })}
            autoComplete='new-password'
            inputProps={{
              'data-testid': 'confirm-new-password',
            }}
            {...registerInput(register, 'confirmPassword')}
            error={
              errors.confirmPassword?.type === 'required'
                ? getErrorProps(intl, errors.confirmPassword).error
                : undefined
            }
            helperText={
              errors.confirmPassword?.type === 'required'
                ? getErrorProps(intl, errors.confirmPassword).helperText
                : undefined
            }
          />
          <PasswordStrength
            currentLevel={calculatePasswordStrength(errors.password, password)}
            error={
              passwordsDifferent
                ? intl.formatMessage({
                    id: 'signUp.errors.passwordsNotMatching',
                  })
                : undefined
            }
          />
          {errors.password && errors.password?.type === 'matches' && (
            <Text variant='h6' fontWeight='fontWeightSemiBold' marginTop={2}>
              {intl.formatMessage({
                id: errors.password.message ?? '',
              })}
            </Text>
          )}
          {errors.password &&
            errors.password?.type !== 'required' &&
            errors.password?.type !== 'matches' && (
              <PasswordRequirements
                errors={errors.password.message?.split('.')}
              />
            )}
          <Text
            variant='h5'
            fontWeight='fontWeightRegular'
            sx={{ color: theme.palette.black[500] }}
          >
            <FormattedMessage
              id='profile.updatePasswordNote'
              values={{
                b: (chunks: ReactNode[]): ReactNode => (
                  <Text
                    component='span'
                    variant='h5'
                    fontWeight='fontWeightSemiBold'
                    sx={{ display: 'inline-flex' }}
                  >
                    {chunks}
                  </Text>
                ),
              }}
            />
          </Text>
          <StyledButton
            disabled={!isValid}
            type='submit'
            data-testid='reset-password-button'
          >
            <FormattedMessage id='misc.save' />
          </StyledButton>
        </Flex>
      </form>
    </>
  );
};

export default PasswordResetRequest;
